/**
 * @author: Claude
 * @date: 2026/03/24
 * @desc: 筛选器方案选择器，用于选择已保存的筛选方案（My Filter）
 */
import React from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import EllipsisHover from '@home/components/EllipsisHover';
import SvgIcon from '@home/components/SvgIcon';
import Select from '@intelligence/v3/components/FormComponents/Select';

import type { ISelectOptionWithGroup } from '@intelligence/v3/components/FormComponents/Select';

import './index.scss';

/** 筛选器方案选项类型 */
export interface IFilterPresetOption {
  /** 方案名称（作为唯一标识） */
  value: string;
  /** 方案显示文本 */
  text: string;
}

interface IFilterPresetSelectProps extends IBaseProps {
  /** 当前选中的筛选器方案名称 */
  value?: string;
  /** 筛选器方案列表 */
  options?: IFilterPresetOption[];
  /** 方案最大显示数量，超过后显示提示 */
  maxDisplayCount?: number;
  /** 选择方案的回调 */
  onChange?: (value: string) => void;
  /** 删除方案的回调 */
  onDelete?: (value: string) => void;
  /** 占位符文本 */
  placeholder?: string;
  /** 无数据文案 */
  emptyText?: string;
  /** 搜索占位符文本 */
  searchPlaceholder?: string;
  /** 删除确认弹窗标题 */
  deleteConfirmTitle?: string;
  /** 删除确认弹窗描述 */
  deleteConfirmDesc?: string;
}

/**
 * 筛选器方案选择器
 * 用于快捷选择已保存的筛选方案（My Filter），支持搜索、删除方案
 */
const FilterPresetSelect: React.FC<IFilterPresetSelectProps> = (props) => {
  const {
    style,
    className,
    value,
    options = [],
    maxDisplayCount = 10,
    onChange,
    onDelete,
    placeholder,
    emptyText,
    searchPlaceholder,
    deleteConfirmTitle,
    deleteConfirmDesc,
  } = props;

  const { formatMessage } = useIntl();

  // 获取选中的方案文本
  const selectedOption = options.find((opt) => opt.value === value);
  const selectedOptionText = selectedOption ? selectedOption.text : '';

  // 转换选项格式
  const selectOptions: ISelectOptionWithGroup[] = options.map((option) => ({
    value: option.value,
    text: option.text,
  }));

  // 默认占位符
  const defaultPlaceholder = placeholder || formatMessage({ id: 'dbi_filterSave_my_filter' });

  // 默认占位符
  const defaultEmptyText = emptyText || formatMessage({ id: 'dbi_search_empty' });

  // 自定义按钮渲染
  const buttonRender = () => {
    return (
      <div className="dbi-select-filter-flex">
        <SvgIcon type="funnel" className="dbi-select-filter-icon" />
        {!value ? (
          <span className="dbi-select-filter-text">{defaultPlaceholder}</span>
        ) : (
          <EllipsisHover className="action-text" text={selectedOptionText} />
        )}
      </div>
    );
  };

  return (
    <Select
      style={style}
      className={classnames('dbi-select-filter', className)}
      boxClassName="dbi-select-filter-box"
      appearance="button"
      searchable
      autoClearSearchValue
      deletable
      optionTextEllipsisHover
      options={selectOptions}
      value={value}
      onChange={onChange}
      onDelete={onDelete}
      maxDisplayCount={maxDisplayCount}
      placeholder={defaultPlaceholder}
      emptyText={defaultEmptyText}
      searchPlaceholder={searchPlaceholder || formatMessage({ id: 'dbi_filterSave_my_filter_search' })}
      deleteConfirmTitle={deleteConfirmTitle}
      deleteConfirmDesc={deleteConfirmDesc}
      listWidth={271}
      listHeight={220}
      button={buttonRender}
    />
  );
};

export default FilterPresetSelect;
